import { Pagination, type PaginationProps } from '~shared/ui/pagination';

import { Loader } from '@breezeventures/ui-kit/loader';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import cn from 'classnames';

import { toggleSort } from './lib/toggle-sort';
import type { DataTableSorting } from './lib/types';
import { BodyCell } from './ui/body-cell';
import { HeaderCell } from './ui/header-cell';

export type DataTablePaginationProps = PaginationProps;

export type DataTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  /** Provide for clickable rows on paginated data; defaults to row index. */
  getRowId?: (row: TData, index: number) => string;
  onRowClick?: (row: TData) => void;
  sorting?: DataTableSorting;
  onSortingChange?: (sorting: DataTableSorting) => void;
  /** Controlled column visibility map keyed by column id. */
  columnVisibility?: VisibilityState;
  pagination?: DataTablePaginationProps;
  /** Forces horizontal scroll when the viewport is narrower than this value. */
  minWidth?: number | string;
  /** Enables vertical scroll with a sticky header. */
  maxHeight?: number | string;
  /** Renders a centered overlay loader on top of the table body. */
  isLoading?: boolean;
  className?: string;
  emptyState?: React.ReactNode;
  /** Renders a sticky chevron column on the right to indicate clickable rows. */
  showRowChevron?: boolean;
};

const DEFAULT_MIN_WIDTH = 640;

export function DataTable<TData>({
  data,
  columns,
  getRowId,
  onRowClick,
  sorting,
  onSortingChange,
  columnVisibility,
  pagination,
  minWidth = DEFAULT_MIN_WIDTH,
  maxHeight,
  isLoading = false,
  className,
  emptyState,
  showRowChevron = false,
}: DataTableProps<TData>) {
  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    state: { columnVisibility },
    manualSorting: true,
    manualPagination: true,
  });

  const handleSortChange = onSortingChange
    ? (columnId: string) => onSortingChange(toggleSort(sorting, columnId))
    : undefined;

  const rows = table.getRowModel().rows;
  const hasRows = rows.length > 0;
  const isScrollable = maxHeight !== undefined;
  const showLoadingPlaceholder = isLoading && !hasRows;

  return (
    <div
      className={cn(
        'flex flex-col gap-6 typography-text-font-family',
        className
      )}
    >
      <div
        className={cn(
          `
            relative overflow-x-auto rounded-lg border border-separator
            bg-elevation-1
          `,
          isScrollable && 'overflow-y-auto'
        )}
        style={isScrollable ? { maxHeight } : undefined}
      >
        {isLoading && (
          <div
            aria-busy
            className={`
              absolute inset-0 z-20 flex items-center justify-center rounded-lg
              bg-elevation-1/60
            `}
          >
            <Loader className="size-8 text-accent-2" />
          </div>
        )}
        <table className="w-full border-collapse" style={{ minWidth }}>
          <colgroup>
            {table.getVisibleLeafColumns().map((column) => {
              const meta = column.columnDef.meta ?? {};
              const style: React.CSSProperties = {};
              if (meta.width !== undefined) style.width = meta.width;
              if (meta.minWidth !== undefined) style.minWidth = meta.minWidth;
              if (meta.maxWidth !== undefined) style.maxWidth = meta.maxWidth;
              return <col key={column.id} style={style} />;
            })}
            {showRowChevron && <col style={{ width: 1 }} />}
          </colgroup>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <HeaderCell
                    key={header.id}
                    header={header}
                    sorting={sorting}
                    onSortChange={handleSortChange}
                    sticky={isScrollable}
                  />
                ))}
                {showRowChevron && (
                  <th
                    aria-hidden="true"
                    style={{ width: 1, whiteSpace: 'nowrap' }}
                    className={`
                      sticky right-0 z-10 border-b border-separator bg-base-gray
                    `}
                  />
                )}
              </tr>
            ))}
          </thead>
          <tbody>
            {hasRows &&
              rows.map((row) => {
                const clickable = !!onRowClick;
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      `
                        group border-b border-separator transition-colors
                        last:border-b-0
                      `,
                      clickable &&
                        `
                          cursor-pointer
                          hover:bg-neutral-1
                        `,
                      clickable &&
                        'focus-visible:bg-neutral-1 focus-visible:outline-none'
                    )}
                    tabIndex={clickable ? 0 : undefined}
                    role={clickable ? 'button' : undefined}
                    onClick={
                      clickable ? () => onRowClick?.(row.original) : undefined
                    }
                    onKeyDown={
                      clickable
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onRowClick?.(row.original);
                            }
                          }
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <BodyCell key={cell.id} cell={cell} />
                    ))}
                    {showRowChevron && (
                      <td
                        aria-hidden="true"
                        style={{ width: 1, whiteSpace: 'nowrap' }}
                        className={`
                          sticky right-0 bg-elevation-1 align-middle
                          transition-colors
                          group-hover:bg-neutral-1
                          group-focus-visible:bg-neutral-1
                        `}
                      >
                        <div className="flex items-center justify-end px-3">
                          <Icon
                            name="chevron-down"
                            size={24}
                            version="v1"
                            className={`size-5 -rotate-90 text-primary`}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            {showLoadingPlaceholder && (
              <tr aria-hidden="true">
                <td colSpan={table.getVisibleLeafColumns().length}>
                  <div className="min-h-[360px]" />
                </td>
              </tr>
            )}
            {!hasRows && !isLoading && emptyState && (
              <tr>
                <td
                  colSpan={table.getVisibleLeafColumns().length}
                  className={`px-4 py-10 text-center text-primitive-secondary`}
                >
                  {emptyState}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && <Pagination {...pagination} />}
    </div>
  );
}
